import { Task, TaskStatus } from '@models/task.model';
import { parseTaskDate, toCalendarISOString } from '@utils/dateOnlyParser';

/**
 * Statuses that mean a task is no longer "in play". Everything else — ACTIVE,
 * INACTIVE, the two approval states — still has work ahead of it.
 */
const CLOSED_STATUSES: ReadonlySet<TaskStatus> = new Set([
  TaskStatus.COMPLETE,
  TaskStatus.CANCELED,
  TaskStatus.REJECTED,
  TaskStatus.DELEGATION_CANCELED,
]);

type AnchorTask = Pick<Task, 'startTime' | 'endTime' | 'status'>;

interface GetScheduleAnchorDateArgs {
  projectStartDate?: string | Date | null;
  projectEndDate?: string | Date | null;
  tasks?: AnchorTask[];
  today?: Date;
}

/**
 * Pick the calendar day the schedule Gantt should open on.
 *
 * - No project dates                       → today
 * - Today before the project starts        → project start
 * - Today inside the project (open bounds) → today
 * - Today after the project ended:
 *     1. earliest start of a task that is still open
 *     2. else the latest end of a COMPLETE task
 *     3. else the project end
 *
 * All comparisons are on calendar days; inputs are date-only strings
 * (YYYY-MM-DD) or Dates and are parsed with the same local-noon rule the
 * rest of the timeline uses, so "2026-09-01" never drifts to Aug 31.
 *
 * @returns YYYY-MM-DD, ready for FullCalendar's `initialDate` / `gotoDate`.
 */
export function getScheduleAnchorDate({
  projectStartDate,
  projectEndDate,
  tasks = [],
  today = new Date(),
}: GetScheduleAnchorDateArgs): string {
  const todayDay = parseTaskDate(today)!;
  const start = parseTaskDate(projectStartDate);
  const end = parseTaskDate(projectEndDate);

  if (start && todayDay < start) {
    return toCalendarISOString(start);
  }

  if (!end || todayDay <= end) {
    return toCalendarISOString(todayDay);
  }

  // Behind schedule / finished: anchor on where the work actually is.
  let nextOpenStart: Date | null = null;
  let lastCompleteEnd: Date | null = null;

  for (const task of tasks) {
    if (!task.status || !CLOSED_STATUSES.has(task.status)) {
      const taskStart = parseTaskDate(task.startTime);
      if (taskStart && (!nextOpenStart || taskStart < nextOpenStart)) {
        nextOpenStart = taskStart;
      }
    } else if (task.status === TaskStatus.COMPLETE) {
      const taskEnd = parseTaskDate(task.endTime);
      if (taskEnd && (!lastCompleteEnd || taskEnd > lastCompleteEnd)) {
        lastCompleteEnd = taskEnd;
      }
    }
  }

  return toCalendarISOString(nextOpenStart ?? lastCompleteEnd ?? end);
}
